import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const EASE = [0.85, 0, 0.15, 1];

const PRODUCTS = [
  {
    name: "Bolsas",
    tag: "Couro & correntes douradas",
    images: [
      "https://images.pexels.com/photos/20591025/pexels-photo-20591025.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      "https://images.unsplash.com/photo-1584917865442-de89df76afd3?q=80&w=940&auto=format&fit=crop",
    ],
    span: "md:col-span-7 h-[420px] sm:h-[520px]",
  },
  {
    name: "Perfumes",
    tag: "Fragrâncias de presença",
    images: [
      "https://images.pexels.com/photos/7850600/pexels-photo-7850600.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      "https://images.unsplash.com/photo-1615634260167-c8cdede054de?q=80&w=940&auto=format&fit=crop",
    ],
    span: "md:col-span-5 h-[420px] sm:h-[520px]",
  },
  {
    name: "Acessórios para Celular",
    tag: "Capinhas, carregadores & mais",
    images: [
      "https://images.unsplash.com/photo-1601784551446-20c9e07cdbdb?q=80&w=940&auto=format&fit=crop",
      "https://images.pexels.com/photos/788946/pexels-photo-788946.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    ],
    span: "md:col-span-4 h-[380px] sm:h-[440px]",
  },
  {
    name: "Utensílios Domésticos",
    tag: "O lar com toque real",
    images: [
      "https://images.unsplash.com/photo-1590794056226-79ef3a8147e1?q=80&w=940&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1556911220-bff31c812dba?q=80&w=940&auto=format&fit=crop",
    ],
    span: "md:col-span-4 h-[380px] sm:h-[440px]",
  },
  {
    name: "Ferramentas",
    tag: "Força para o trabalho",
    images: [
      "https://images.unsplash.com/photo-1530124566582-a618bc2615dc?q=80&w=940&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1504148455328-c376907d081c?q=80&w=940&auto=format&fit=crop",
    ],
    span: "md:col-span-4 h-[380px] sm:h-[440px]",
  },
  {
    name: "Maquiagens",
    tag: "Beleza que impõe respeito",
    images: [
      "https://images.unsplash.com/photo-1596462502278-27bfdc403348?q=80&w=940&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1512496015851-a90fb38ba796?q=80&w=940&auto=format&fit=crop",
    ],
    span: "md:col-span-4 h-[380px] sm:h-[440px]",
  },
  {
    name: "Relógios",
    tag: "Pontualidade com brilho",
    images: [
      "https://images.unsplash.com/photo-1524592094714-0f0654e20314?q=80&w=940&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1434056886845-dac89ffe9b56?q=80&w=940&auto=format&fit=crop",
    ],
    span: "md:col-span-4 h-[380px] sm:h-[440px]",
  },
  {
    name: "Itens para Festa",
    tag: "Balões, decoração & confete",
    images: [
      "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?q=80&w=940&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1513151233558-d860c5398176?q=80&w=940&auto=format&fit=crop",
    ],
    span: "md:col-span-4 h-[380px] sm:h-[440px]",
  },
  {
    name: "Personalização & Sublimação",
    tag: "Canecas, camisetas & brindes únicos",
    images: [
      "https://images.unsplash.com/photo-1572119865084-43c285814d63?q=80&w=940&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?q=80&w=940&auto=format&fit=crop",
    ],
    span: "md:col-span-12 h-[340px] sm:h-[400px]",
  },
];

const CardCarousel = ({ images, name, offset }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    let interval;
    const timeout = setTimeout(() => {
      interval = setInterval(() => setIndex((i) => (i + 1) % images.length), 7000);
    }, offset);
    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [images.length, offset]);

  return (
    <div className="absolute inset-0 overflow-hidden transition-transform duration-[1200ms] ease-out group-hover:scale-105">
      <AnimatePresence>
        <motion.img
          key={index}
          src={images[index]}
          alt={name}
          loading="lazy"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.4, ease: "easeInOut" }}
          className="absolute inset-0 h-full w-full object-cover brightness-[0.82] saturate-[0.95]"
        />
      </AnimatePresence>
      <div className="absolute bottom-5 right-5 z-10 flex gap-1.5">
        {images.map((_, i) => (
          <span
            key={i}
            className={`h-1 rounded-full transition-all duration-500 ${
              i === index ? "w-5 bg-gold" : "w-2 bg-white/40"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

const ProductCard = ({ product, index }) => (
  <motion.article
    data-testid={`product-card-${index}`}
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, delay: 0.08 * (index % 3), ease: EASE }}
    className={`group relative col-span-1 overflow-hidden ${product.span}`}
  >
    <CardCarousel images={product.images} name={product.name} offset={index * 900} />
    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent transition-opacity duration-700 group-hover:via-[#050505]/80" />
    <div className="absolute inset-0 bg-royal/0 transition-colors duration-700 group-hover:bg-royal/10" />

    <span className="absolute right-5 top-5 rounded-full border border-gold/40 bg-black/50 px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-gold backdrop-blur-sm">
      Em breve
    </span>

    <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
      <span className="mb-3 block h-px w-10 bg-royal transition-all duration-700 group-hover:w-24 group-hover:bg-gold" />
      <span className="block text-[11px] font-semibold uppercase tracking-[0.3em] text-neutral-400 transition-colors duration-500 group-hover:text-gold">
        {String(index + 1).padStart(2, "0")} — {product.tag}
      </span>
      <h3 className="mt-2 font-serif text-3xl font-semibold text-white sm:text-4xl">
        {product.name}
      </h3>
    </div>
  </motion.article>
);

export const ProductGrid = () => (
  <section data-testid="products-section" className="relative mx-auto max-w-7xl px-5 py-28 sm:px-8 sm:py-36">
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 1, ease: EASE }}
      className="mb-16 grid grid-cols-1 gap-6 md:grid-cols-12"
    >
      <p className="text-[11px] font-semibold uppercase tracking-[0.5em] text-royal md:col-span-3">
        A Vitrine Real
      </p>
      <h2 className="font-serif text-3xl tracking-tight text-white sm:text-4xl lg:text-5xl md:col-span-8 md:col-start-5">
        O que você vai encontrar <span className="gold-text italic">no reino</span>
      </h2>
    </motion.div>

    <div className="grid grid-cols-1 gap-4 md:grid-cols-12 sm:gap-5">
      {PRODUCTS.map((product, index) => (
        <ProductCard key={product.name} product={product} index={index} />
      ))}
    </div>
  </section>
);
